/**
 * GYM-1592: Workspace-driven mobile bottom navigation + full-height drawer.
 *
 * Bottom bar shows the top ≤4 main workspaces + the Admin gear (= ≤5 total).
 * Tapping a workspace with sections opens a grouped drawer; single-destination
 * workspaces navigate directly.  The drawer closes on scrim-tap, Escape, or
 * when a nav link is activated.
 */

import { Grid2X2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import {
  NAV_CONFIG,
  visibleSections,
  visibleWorkspaces,
  workspaceForPath,
} from "@/app/layout/nav-config";
import type { Workspace } from "@/app/layout/nav-config";
import { useAuthStore } from "@/lib/auth/store";
import { useEffectivePermissions } from "@/lib/auth/permissions";
import { useNavStore } from "@/lib/nav/store";
import { cn } from "@/lib/utils/cn";

function isItemActive(to: string, pathname: string): boolean {
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(to + "/");
}

export function MobileBottomNav() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isSuperuser = useAuthStore((s) => s.user?.is_superuser ?? false);
  const { byCode } = useEffectivePermissions();

  const has = (key: string) => isSuperuser || key in byCode;
  const setLastPath = useNavStore((s) => s.setLastPath);

  const allVisible = visibleWorkspaces(NAV_CONFIG, has);
  const nonAdminWorkspaces = allVisible.filter((ws) => ws.key !== "admin");
  const adminWorkspace = allVisible.find((ws) => ws.key === "admin");

  // Show up to 4 main workspaces in the bar; overflow goes into a "More" drawer
  const mainWorkspaces = nonAdminWorkspaces.slice(0, 4);
  const overflowWorkspaces = nonAdminWorkspaces.slice(4);
  const hasOverflow = overflowWorkspaces.length > 0;

  const tabWorkspaces: Workspace[] = adminWorkspace
    ? [...mainWorkspaces, adminWorkspace]
    : mainWorkspaces;

  const activeWorkspace = workspaceForPath(pathname);

  const [drawerKey, setDrawerKey] = useState<string | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  // Refs to the bottom-nav buttons so we can restore focus on drawer close
  const triggerRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const drawerWs = drawerKey ? NAV_CONFIG.find((ws) => ws.key === drawerKey) : null;
  const drawerSections = drawerWs ? visibleSections(drawerWs.sections, has) : [];

  function openDrawer(ws: Workspace) {
    if (ws.sections.length === 0) {
      if (activeWorkspace && activeWorkspace.key !== ws.key) {
        setLastPath(activeWorkspace.key, pathname);
      }
      navigate(ws.defaultPath);
      setDrawerKey(null);
    } else {
      setDrawerKey((prev) => (prev === ws.key ? null : ws.key));
    }
  }

  function closeDrawer() {
    setDrawerKey(null);
  }

  function closeMore() {
    setMoreOpen(false);
  }

  // Close drawers on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") { closeDrawer(); closeMore(); }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Close drawers when route changes (link activated)
  useEffect(() => {
    closeDrawer();
    closeMore();
  }, [pathname]);

  // Focus trap + initial focus for workspace drawer
  useEffect(() => {
    if (!drawerKey || !drawerRef.current) return;
    const el = drawerRef.current;
    const focusable = Array.from(
      el.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'),
    );
    focusable[0]?.focus();
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Tab" || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    el.addEventListener("keydown", onKeyDown);
    return () => el.removeEventListener("keydown", onKeyDown);
  }, [drawerKey]);

  // Restore focus to triggering button when workspace drawer closes
  const prevDrawerKey = useRef<string | null>(null);
  useEffect(() => {
    if (prevDrawerKey.current && !drawerKey) {
      triggerRefs.current.get(prevDrawerKey.current)?.focus();
    }
    prevDrawerKey.current = drawerKey;
  }, [drawerKey]);

  // Focus trap + initial focus for More drawer
  useEffect(() => {
    if (!moreOpen || !moreRef.current) return;
    const el = moreRef.current;
    const focusable = Array.from(
      el.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'),
    );
    focusable[0]?.focus();
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Tab" || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    el.addEventListener("keydown", onKeyDown);
    return () => el.removeEventListener("keydown", onKeyDown);
  }, [moreOpen]);

  const drawerOpen = drawerKey !== null;

  return (
    <>
      {/* More overflow drawer ────────────────────────────────────────────── */}
      {moreOpen && hasOverflow && (
        <>
          <div
            aria-hidden="true"
            className="fixed inset-0 z-50 bg-black/40 md:hidden"
            onClick={closeMore}
          />
          <div
            ref={moreRef}
            role="dialog"
            aria-modal="true"
            aria-label={t("nav.moreWorkspaces", "More")}
            className={cn(
              "fixed inset-x-3 bottom-[calc(4.75rem+var(--toolbar-inset,0px)+env(safe-area-inset-bottom,0px))] z-50 flex flex-col bg-surface md:hidden rounded-3xl",
              "shadow-elev-3 overflow-hidden motion-reduce:transition-none",
            )}
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          >
            <div className="flex items-center justify-between border-b border-border-100 px-4 py-3">
              <span className="text-base font-semibold text-ink-900">
                {t("nav.moreWorkspaces", "More")}
              </span>
              <button
                type="button"
                aria-label={t("nav.closeDrawer")}
                onClick={closeMore}
                className={cn(
                  "flex h-9 w-9 items-center justify-center rounded-sm text-ink-500",
                  "hover:bg-ink-300/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
                )}
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <nav
              aria-label={t("nav.moreWorkspaces", "More")}
              className="overflow-y-auto px-2 py-3 space-y-0.5"
            >
              {overflowWorkspaces.map((ws) => {
                const isActive = activeWorkspace?.key === ws.key;
                return (
                  <button
                    key={ws.key}
                    type="button"
                    aria-current={isActive ? "page" : undefined}
                    onClick={() => { closeMore(); openDrawer(ws); }}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-sm px-3 py-3 text-sm min-h-[44px] transition-colors",
                      isActive
                        ? "bg-primary-500/10 text-primary-700 dark:text-primary-300 font-medium"
                        : "text-ink-700 hover:bg-ink-300/15",
                    )}
                  >
                    <ws.icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                    <span>{t(ws.labelKey)}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </>
      )}

      {/* Bottom-sheet drawer ─────────────────────────────────────────────── */}
      {drawerOpen && (
        <>
          {/* Scrim */}
          <div
            aria-hidden="true"
            className="fixed inset-0 z-50 bg-black/30 backdrop-blur-[2px] md:hidden"
            onClick={closeDrawer}
          />

          {/* Sheet panel — compact partial-height bottom sheet */}
          <div
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label={drawerWs ? t(drawerWs.labelKey) : undefined}
            className="fixed inset-x-3 bottom-[calc(4.75rem+var(--toolbar-inset,0px)+env(safe-area-inset-bottom,0px))] z-50 flex flex-col md:hidden rounded-3xl bg-white dark:bg-surface shadow-[0_-8px_40px_rgba(0,0,0,0.18)] overflow-hidden max-h-[72vh]"
            style={{
              animation: "slideUpSheet 0.32s cubic-bezier(0.32, 0.72, 0, 1) forwards",
              paddingBottom: "env(safe-area-inset-bottom)",
            }}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1 shrink-0">
              <div className="w-9 h-1 rounded-full bg-ink-200 dark:bg-white/20" />
            </div>

            {/* Gradient header */}
            <div className="flex items-center justify-between px-4 pt-1 pb-2 shrink-0">
              <div className="flex items-center gap-2">
                {drawerWs && (
                  <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-b from-primary-50 to-primary-100/80 border border-primary-200/60 dark:from-primary-900/40 dark:to-primary-900/20 dark:border-primary-500/20">
                    <drawerWs.icon className="h-3.5 w-3.5 text-primary-600 dark:text-primary-400" aria-hidden="true" />
                  </div>
                )}
                <span className="text-sm font-bold text-ink-900 dark:text-ink-100 tracking-tight">
                  {drawerWs ? t(drawerWs.labelKey) : ""}
                </span>
              </div>
              <button
                type="button"
                aria-label={t("nav.closeDrawer")}
                onClick={closeDrawer}
                className="flex h-7 w-7 items-center justify-center rounded-full bg-ink-100 dark:bg-white/10 text-ink-500 hover:bg-ink-200 dark:hover:bg-white/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>

            <div className="mx-4 h-px bg-ink-100 dark:bg-white/[0.08] shrink-0" />

            {/* Compact nav sections */}
            <nav
              aria-label={drawerWs ? t(drawerWs.labelKey) : undefined}
              className="flex-1 overflow-y-auto px-2 py-2 space-y-3"
            >
              {drawerSections.map((section) => (
                <div key={section.key}>
                  <div className="px-3 mb-0.5 text-[10px] font-bold uppercase tracking-widest text-ink-400 dark:text-ink-500 select-none">
                    {t(section.labelKey)}
                  </div>
                  <div className="space-y-0.5">
                    {section.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.to === "/"}
                        aria-current={isItemActive(item.to, pathname) ? "page" : undefined}
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm min-h-[40px] transition-colors",
                            isActive
                              ? "bg-primary-500/10 text-primary-700 dark:text-primary-300 font-semibold"
                              : "text-ink-600 dark:text-ink-300 hover:bg-ink-50 dark:hover:bg-white/[0.06]",
                          )
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <div className={cn(
                              "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors",
                              isActive
                                ? "bg-primary-500/15 dark:bg-primary-500/20"
                                : "bg-ink-100/80 dark:bg-white/[0.06]",
                            )}>
                              <item.icon className={cn("h-3.5 w-3.5", isActive ? "text-primary-600 dark:text-primary-400" : "text-ink-500 dark:text-ink-400")} aria-hidden="true" />
                            </div>
                            <span>{t(item.labelKey)}</span>
                          </>
                        )}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ))}
            </nav>
          </div>
        </>
      )}

      {/* Bottom navigation: floating minimal capsule ─────────────────────── */}
      <nav
        aria-label={t("nav.mobileBottomNav")}
        className="pointer-events-none fixed inset-x-0 bottom-[calc(0.75rem+var(--toolbar-inset,0px)+env(safe-area-inset-bottom,0px))] z-40 flex justify-center px-4 md:hidden"
      >
        <div
          className={cn(
            "pointer-events-auto flex max-w-full items-center gap-1 rounded-full p-1.5",
            // dark glass capsule with a lit top edge, readable on light and dark pages
            "bg-[rgb(14_30_26/0.88)] backdrop-blur-xl border border-white/[0.10]",
            "shadow-[0_12px_32px_rgba(0,0,0,0.35),0_2px_6px_rgba(0,0,0,0.2),inset_0_1px_0_rgba(255,255,255,0.14)]",
          )}
        >
          {tabWorkspaces.map((ws) => {
            const isActive = activeWorkspace?.key === ws.key;
            const isDrawerOpen = drawerKey === ws.key;
            const on = isActive || isDrawerOpen;

            return (
              <button
                key={ws.key}
                ref={(el) => { if (el) triggerRefs.current.set(ws.key, el); else triggerRefs.current.delete(ws.key); }}
                type="button"
                aria-label={t(ws.labelKey)}
                aria-current={isActive ? "page" : undefined}
                aria-expanded={ws.sections.length > 0 ? isDrawerOpen : undefined}
                onClick={() => openDrawer(ws)}
                className={cn(
                  "flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-full transition-all duration-200 active:scale-95",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300",
                  on
                    ? [
                        "bg-gradient-to-b from-primary-500 to-primary-700 px-3.5 text-white",
                        "shadow-[0_4px_14px_rgb(var(--color-primary-500)/0.55),inset_0_1px_0_rgba(255,255,255,0.35),inset_0_-2px_3px_rgba(0,0,0,0.2)]",
                      ]
                    : "w-10 text-white/60 hover:text-white",
                )}
              >
                <ws.icon className="h-[18px] w-[18px]" aria-hidden="true" />
                {on && (
                  <span className="max-w-[88px] truncate text-xs font-semibold leading-none">{t(ws.labelKey)}</span>
                )}
              </button>
            );
          })}

          {/* More — shown only when there are overflow workspaces */}
          {hasOverflow && (
            <button
              type="button"
              aria-expanded={moreOpen}
              aria-label={t("nav.moreWorkspaces", "More")}
              onClick={() => setMoreOpen((v) => !v)}
              className={cn(
                "flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-full transition-all duration-200 active:scale-95",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300",
                moreOpen
                  ? [
                      "bg-gradient-to-b from-primary-500 to-primary-700 px-3.5 text-white",
                      "shadow-[0_4px_14px_rgb(var(--color-primary-500)/0.55),inset_0_1px_0_rgba(255,255,255,0.35),inset_0_-2px_3px_rgba(0,0,0,0.2)]",
                    ]
                  : "w-10 text-white/60 hover:text-white",
              )}
            >
              <Grid2X2 className="h-[18px] w-[18px]" aria-hidden="true" />
              {moreOpen && <span className="text-xs font-semibold leading-none">{t("nav.moreWorkspaces", "More")}</span>}
            </button>
          )}
        </div>
      </nav>
    </>
  );
}
